/**
 * dayOf.ts — the single source of truth for "is there a show TODAY?" (P2e).
 *
 * Before P2e, DayOfPage computed "today" itself (local clock → 'YYYY-MM-DD'
 * string key compared against `tour_dates.date`). The contextual band landing
 * (BandLandingPage) needs the exact same answer, so the rule lives HERE and
 * both call `findShowToday` — no second copy that could drift (e.g. one side
 * parsing dates as UTC and silently disagreeing around midnight).
 *
 * Rules honoured (never broken):
 *   - "today" = the CLIENT's local date (`new Date()` → getFullYear/Month/Date,
 *     never `toISOString()` which is UTC and shifts the day for most of the
 *     world);
 *   - `date` values are plain 'YYYY-MM-DD' strings, compared as strings —
 *     `new Date('2026-10-09')` would parse as UTC midnight (see localFromKey).
 */

/** 'YYYY-MM-DD' for a Date in the LOCAL timezone (string-compared, never parsed). */
const pad2 = (n: number) => String(Math.max(0, Math.floor(n))).padStart(2, '0');

export function localDateKey(d: Date): string {
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
}

/** Today's local date key at timestamp `now` (defaults to the current clock). */
export function todayKey(now: number = Date.now()): string {
  return localDateKey(new Date(now));
}

/**
 * The show dated TODAY (local date of `now`), else null.
 * Used by the Day-of page (its hero/ROS) AND by the contextual band landing
 * (Day-of vs Atlas) — one algorithm, one behaviour, everywhere.
 */
export function findShowToday<T extends { date?: string | null }>(
  shows: readonly T[],
  now: number = Date.now(),
): T | null {
  const key = todayKey(now);
  return shows.find((s) => !!s.date && s.date === key) ?? null;
}

/* ------------------------------------------------------------------ */
/* Run-of-show NOW marker resolution (DayOfPage).                       */
/*                                                                     */
/* "NOW" = the last step that has started and not ended. A step without */
/* an end time is normally current until the next one starts (kept),    */
/* because a later start always takes over. But if that open-ended step */
/* is the LAST step of the run there is no later start to relieve it —  */
/* it would stay "now" forever (e.g. a "Sound curfew" entered with only */
/* a start hour). Decision (b): a final open-ended step keeps NOW for    */
/* LAST_OPEN_ENDED_WINDOW_MIN after its own start, then the run is done */
/* (no NOW marker — all started steps become done). A real clock time is */
/* NEVER invented: only time_from + the window is used.                  */
/* ------------------------------------------------------------------ */

/** Minutes after its own start during which the final open-ended step of a
 *  run may keep the NOW marker before the run is treated as done. */
export const LAST_OPEN_ENDED_WINDOW_MIN = 120;

/** Minimal shape of a step for NOW resolution (DayOfPage's Draft satisfies it). */
export interface RunStepLike {
  start: number | null;
  started: boolean;
  ended: boolean;
  end: number | null;
}

/**
 * Index of the run-of-show step currently showing "NOW", or -1 when no step
 * is current (all timed steps done / not started yet / untimed placeholder).
 */
export function resolveNowIndex(
  steps: readonly RunStepLike[],
  currentMinutes: number,
): number {
  const lastTimed = steps.reduce(
    (acc, s, i) => (s.start != null ? i : acc),
    -1,
  );
  if (lastTimed >= 0) {
    const fin = steps[lastTimed];
    if (
      fin.start != null &&
      fin.started &&
      fin.end == null &&
      currentMinutes >= fin.start + LAST_OPEN_ENDED_WINDOW_MIN
    ) {
      // Final open-ended step past its window → the run is done, no NOW.
      return -1;
    }
  }
  let nowIdx = -1;
  steps.forEach((s, i) => {
    if (s.start != null && s.started && !s.ended) nowIdx = i;
  });
  return nowIdx;
}
